import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import type { Feature } from 'geojson';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { FloatingPanelComponent } from '../floating-panel/floating-panel.component';
import { VlmFeatureListComponent } from './vlm-feature-list.component';
import { VlmLayerPanelService } from './vlm-layer-panel.service';
import { VlmFeatureDialogComponent } from './vlm-feature-dialog.component';
import { ApiViewerService } from '../../viewer-components/services/internal-services/api-viewer.service';
import { MapInteractionService } from '../../ai-components/chat/services/map-interaction.service';
import { GeoService } from '../../viewer-components/services/internal-services/geo.service';
import { getVlmBlock } from './vlm-layer-panel.types';

/**
 * Floating panel for VLM analysis results. Place inside a `position: relative` container.
 * Visibility is driven by VlmLayerPanelService.
 * Also handles opening individual-feature dialogs triggered by map clicks.
 */
@Component({
  selector: 'lib-vlm-layer-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FloatingPanelComponent, VlmFeatureListComponent],
  template: `
    @if (panelService.panelOpen() && panelService.registeredLayer(); as layer) {
      <lib-floating-panel [title]="layer.layerName" (closed)="panelService.close()">
        <lib-vlm-feature-list
          [features]="layer.features"
          [authUrlFn]="authUrlFn"
          (navigateTo)="onNavigateTo($event)" />
      </lib-floating-panel>
    }
  `,
})
export class VlmLayerPanelComponent implements OnInit, OnDestroy {
  protected readonly panelService = inject(VlmLayerPanelService);
  private readonly apiViewer = inject(ApiViewerService);
  private readonly mapInteraction = inject(MapInteractionService);
  private readonly dialog = inject(MatDialog);
  private readonly subscriptions: Subscription[] = [];

  protected readonly authUrlFn = (url: string): string => {
    if (!url) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}${this.apiViewer.queryAuth}`;
  };

  ngOnInit(): void {
    this.subscriptions.push(
      this.panelService.featureDetailTrigger$.subscribe((feature) => {
        const vlm = getVlmBlock(feature);
        if (!vlm) {
          console.warn('VlmLayerPanelComponent: clicked feature has no VLM data block');
          return;
        }
        this.dialog.open(VlmFeatureDialogComponent, {
          data: { vlm, authUrlFn: this.authUrlFn },
          autoFocus: false,
        });
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  protected onNavigateTo(feature: Feature): void {
    if (!feature.geometry) {
      console.warn('VlmLayerPanelComponent: navigate on feature with no geometry');
      return;
    }
    try {
      // Features stored in the panel come from prepareGeoJson which converts to EPSG:3857.
      // zoomToExtent expects WGS84 lon/lat, so convert first.
      const wgs84Feature = GeoService.toWgs84(feature, false);
      const [minX, minY, maxX, maxY] = GeoService.bbox(wgs84Feature.geometry);
      this.mapInteraction.zoomToExtent(minX, minY, maxX, maxY);
    } catch (err) {
      console.error('VlmLayerPanelComponent: failed to compute bbox for navigation', err);
    }
  }
}
